import { LottolandSmileLogo } from '@shared/icons';
import type { InboxMessage as InboxMessageType } from '@shared/providers';
import { ROUTES } from '@shared/utils';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import { NotificationBubble } from '../../../NotificationBubble/NotificationBubble';

type InboxMessageProps = {
  message: InboxMessageType;
};

function InboxMessage({ message }: InboxMessageProps) {
  const t = useTranslations('my_account.inbox');
  const { title, executionDate, readDate } = message;
  const isUnread = !readDate;
  const date = t('date', { date: new Date(executionDate) });
  const ariaLabel = isUnread ? t('accessibility.unread_message') : t('accessibility.read_message', { title, date });

  return (
    <Link
      href={ROUTES.MY_ACCOUNT.INBOX_MESSAGE(message.id)}
      className="flex gap-4 items-center w-full py-5 hover:bg-container-hover focus:bg-container-hover focus:outline-none focus:ring-2 focus:ring-accent-primary focus:ring-offset-2 transition-colors duration-150 rounded-md text-left cursor-pointer no-underline"
      aria-label={ariaLabel}
      data-testid="inbox-message"
      data-track-name={'Message'}
      data-track-title={message.title}
      data-track-status={isUnread ? 'Unread' : 'Read'}
      data-track-date={message.executionDate.split('T')[0]} // e.g. 2025-08-25 | YYYY-MM-DD
    >
      <div className="relative flex items-center h-10 w-10 p-2 bg-container-accent rounded-md border-border" aria-hidden="true">
        <LottolandSmileLogo className="text-accent-green dark:text-foreground-inverse" />
        {isUnread ? <NotificationBubble /> : null}
      </div>
      <div className="grow">
        <div className="flex gap-2.75">
          <span className="flex-1 min-w-0 paragraph-strong line-clamp-1" aria-hidden="true">
            {title}
          </span>
          <span className="paragraph-s-strong text-foreground-subtle" aria-hidden="true">
            {date}
          </span>
        </div>
      </div>
    </Link>
  );
}

export { InboxMessage };
